import React from 'react';
import Icon from 'components/AppIcon';

const FinancialsSlide = () => {
  const revenueProjections = [
    {
      year: 'Year 1',
      period: '2024',
      lawyers: 500,
      clients: 10000,
      subscriptionRevenue: 30000000,
      transactionRevenue: 15000000,
      serviceRevenue: 5000000,
      totalRevenue: 50000000,
      costs: 40000000,
      netIncome: 10000000,
      growth: 'Launch'
    },
    {
      year: 'Year 2',
      period: '2025',
      lawyers: 2000,
      clients: 50000,
      subscriptionRevenue: 120000000,
      transactionRevenue: 60000000,
      serviceRevenue: 20000000,
      totalRevenue: 200000000,
      costs: 140000000,
      netIncome: 60000000,
      growth: '+300%'
    },
    {
      year: 'Year 3',
      period: '2026',
      lawyers: 5000,
      clients: 150000,
      subscriptionRevenue: 300000000,
      transactionRevenue: 150000000,
      serviceRevenue: 50000000,
      totalRevenue: 500000000,
      costs: 300000000,
      netIncome: 200000000,
      growth: '+150%'
    }
  ];

  const keyMetrics = [
    {
      metric: 'Customer Acquisition Cost (CAC)',
      year1: 'KES 15,000',
      year2: 'KES 12,000',
      year3: 'KES 10,000',
      icon: 'TrendingDown'
    },
    {
      metric: 'Lifetime Value (LTV)',
      year1: 'KES 180,000',
      year2: 'KES 240,000',
      year3: 'KES 300,000',
      icon: 'TrendingUp'
    },
    {
      metric: 'LTV/CAC Ratio',
      year1: '12:1',
      year2: '20:1',
      year3: '30:1',
      icon: 'Award'
    },
    {
      metric: 'Monthly Churn Rate',
      year1: '8%',
      year2: '5%',
      year3: '3%',
      icon: 'TrendingDown'
    }
  ];

  const costBreakdown = [
    {
      category: 'Technology & Development',
      percentage: 35,
      amount: 105000000,
      description: 'Platform development, maintenance, infrastructure'
    },
    {
      category: 'Sales & Marketing',
      percentage: 25,
      amount: 75000000,
      description: 'Customer acquisition, brand building, advertising'
    },
    {
      category: 'Operations',
      percentage: 20,
      amount: 60000000,
      description: 'Customer support, legal compliance, administration'
    },
    {
      category: 'General & Administrative',
      percentage: 20,
      amount: 60000000,
      description: 'Management, finance, HR, corporate functions'
    }
  ];

  const fundingRequirements = [
    {
      purpose: 'Technology Enhancement',
      amount: 40000000,
      percentage: 40,
      description: 'Platform scaling, mobile app, AI features'
    },
    {
      purpose: 'Marketing & Sales',
      amount: 30000000,
      percentage: 30,
      description: 'Customer acquisition, brand building'
    },
    {
      purpose: 'Team Expansion',
      amount: 20000000,
      percentage: 20,
      description: 'Hiring key personnel across all departments'
    },
    {
      purpose: 'Working Capital',
      amount: 10000000,
      percentage: 10,
      description: 'Operating expenses and reserves'
    }
  ];

  const formatCurrency = (amount) => {
    if (amount >= 1000000) {
      return `KES ${(amount / 1000000).toFixed(0)}M`;
    }
    return `KES ${amount.toLocaleString()}`;
  };

  return (
    <div className="py-12">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-heading font-bold text-text-primary mb-4">
            Financial Projections
          </h1>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Strong unit economics with path to profitability and sustainable growth
          </p>
        </div>

        {/* Revenue Projections */}
        <div className="mb-12">
          <h2 className="text-2xl font-heading font-bold text-text-primary mb-8 text-center">
            3-Year Revenue Projections
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {revenueProjections?.map((projection, index) => (
              <div key={index} className="bg-gradient-to-br from-primary-50 to-secondary-50 rounded-lg p-6 border border-primary-200">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-heading font-bold text-text-primary">
                    {projection?.year}
                  </h3>
                  <div className="bg-primary text-white px-3 py-1 rounded-full text-sm font-medium">
                    {projection?.growth}
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-text-secondary">Lawyers</span>
                    <span className="font-semibold text-text-primary">{projection?.lawyers?.toLocaleString()}</span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-text-secondary">Clients</span>
                    <span className="font-semibold text-text-primary">{projection?.clients?.toLocaleString()}</span>
                  </div>
                  
                  <div className="border-t border-primary-200 pt-4">
                    <div className="flex justify-between mb-2">
                      <span className="text-text-secondary text-sm">Subscription</span>
                      <span className="text-sm text-text-primary">{formatCurrency(projection?.subscriptionRevenue)}</span>
                    </div>
                    
                    <div className="flex justify-between mb-2">
                      <span className="text-text-secondary text-sm">Transaction</span>
                      <span className="text-sm text-text-primary">{formatCurrency(projection?.transactionRevenue)}</span>
                    </div>
                    
                    <div className="flex justify-between mb-4">
                      <span className="text-text-secondary text-sm">Services</span>
                      <span className="text-sm text-text-primary">{formatCurrency(projection?.serviceRevenue)}</span>
                    </div>
                    
                    <div className="flex justify-between border-t border-primary-200 pt-2">
                      <span className="font-semibold text-text-primary">Total Revenue</span>
                      <span className="font-bold text-primary">{formatCurrency(projection?.totalRevenue)}</span>
                    </div>
                    
                    <div className="flex justify-between mt-2">
                      <span className="text-text-secondary">Net Income</span>
                      <span className="font-semibold text-success-600">{formatCurrency(projection?.netIncome)}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Key Metrics */}
        <div className="mb-12">
          <h2 className="text-2xl font-heading font-bold text-text-primary mb-8 text-center">
            Key Business Metrics
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {keyMetrics?.map((metric, index) => (
              <div key={index} className="bg-surface rounded-lg p-6 shadow-card border border-border">
                <div className="w-12 h-12 bg-secondary-100 rounded-lg flex items-center justify-center mb-4">
                  <Icon name={metric?.icon} size={24} className="text-secondary-600" />
                </div>
                <h3 className="font-heading font-semibold text-text-primary mb-4 text-sm">
                  {metric?.metric}
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-xs">
                    <span className="text-text-secondary">Y1</span>
                    <span className="font-medium text-text-primary">{metric?.year1}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-text-secondary">Y2</span>
                    <span className="font-medium text-text-primary">{metric?.year2}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-text-secondary">Y3</span>
                    <span className="font-bold text-primary">{metric?.year3}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Cost Structure */}
        <div className="mb-12">
          <h2 className="text-2xl font-heading font-bold text-text-primary mb-8 text-center">
            Cost Structure (Year 3)
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-4">
              {costBreakdown?.map((cost, index) => (
                <div key={index} className="bg-surface rounded-lg p-4 shadow-card border border-border">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-heading font-semibold text-text-primary">
                      {cost?.category}
                    </h3>
                    <div className="text-right">
                      <div className="text-lg font-bold text-primary">{cost?.percentage}%</div>
                      <div className="text-sm text-text-secondary">{formatCurrency(cost?.amount)}</div>
                    </div>
                  </div>
                  <p className="text-sm text-text-secondary">
                    {cost?.description}
                  </p>
                </div>
              ))}
            </div>
            
            <div className="flex items-center justify-center">
              <div className="text-center">
                <div className="text-5xl font-heading font-bold text-primary mb-4">
                  {formatCurrency(300000000)}
                </div>
                <p className="text-xl text-text-secondary mb-4">Total Operating Costs</p>
                <div className="text-2xl font-heading font-bold text-success-600 mb-2">40%</div>
                <p className="text-text-secondary">Net Profit Margin</p>
              </div>
            </div>
          </div>
        </div>

        {/* Funding Requirements */}
        <div className="bg-surface rounded-lg p-8 shadow-card border border-border">
          <h2 className="text-2xl font-heading font-bold text-text-primary mb-8 text-center">
            Funding Requirements: KES 100M
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {fundingRequirements?.map((requirement, index) => (
              <div key={index} className="text-center">
                <div className="w-20 h-20 bg-primary rounded-xl flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-white">{requirement?.percentage}%</span>
                </div>
                <h3 className="font-heading font-semibold text-text-primary mb-2">
                  {requirement?.purpose}
                </h3>
                <div className="text-lg font-bold text-primary mb-2">
                  {formatCurrency(requirement?.amount)}
                </div>
                <p className="text-sm text-text-secondary">
                  {requirement?.description}
                </p>
              </div>
            ))}
          </div>
          
          <div className="bg-gradient-to-r from-primary-50 to-secondary-50 rounded-lg p-6">
            <h3 className="text-xl font-heading font-bold text-text-primary mb-4 text-center">
              Expected Returns
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
              <div>
                <div className="text-2xl font-heading font-bold text-primary mb-2">18 months</div>
                <p className="text-text-secondary">Break-even timeline</p>
              </div>
              <div>
                <div className="text-2xl font-heading font-bold text-primary mb-2">5-10x</div>
                <p className="text-text-secondary">Projected ROI in 5 years</p>
              </div>
              <div>
                <div className="text-2xl font-heading font-bold text-primary mb-2">30%+</div>
                <p className="text-text-secondary">Annual growth rate</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialsSlide;